import { useState, useEffect, useCallback, useRef } from 'react'
import { api, monitoringApi, SystemMonitoringResponse } from '@/lib/api'

interface ServerMetrics {
  cpu_usage: number
  memory_usage: number
  request_count: number
  response_time_avg: number
  error_rate: number
  cache_hit_rate?: number
  active_connections: number
  uptime: number
  // Real system metrics
  process_id?: number
  memory_mb?: number
  process_status?: string
  monitoring_source?: 'real_system_metrics' | 'simulated_metrics'
}

interface ServerStatus {
  server_id: string
  status: 'running' | 'stopped' | 'starting' | 'error'
  port: number
  last_activity: string
  metrics: ServerMetrics
  logs: Array<{
    timestamp: string
    level: 'info' | 'warn' | 'error'
    message: string
    source?: string
  }>
  // Real system monitoring data
  system_metrics?: {
    cpu_percent: number
    memory_percent: number
    memory_total: number
  }
}

interface UseServerMonitoringOptions {
  serverId: string
  enabled?: boolean
  interval?: number // polling interval in ms
  onStatusChange?: (status: ServerStatus) => void
  onError?: (error: Error) => void
}

export function useServerMonitoring({
  serverId,
  enabled = true,
  interval = 10000, // Increased to 10 seconds to reduce API calls
  onStatusChange,
  onError
}: UseServerMonitoringOptions) {
  const [status, setStatus] = useState<ServerStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [systemMonitoring, setSystemMonitoring] = useState<SystemMonitoringResponse | null>(null)
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastFetchRef = useRef<number>(0)
  const callbacksRef = useRef({ onStatusChange, onError })

  // Update callbacks without triggering effects
  useEffect(() => {
    callbacksRef.current = { onStatusChange, onError }
  }, [onStatusChange, onError])

  const fetchServerStatus = useCallback(async () => {
    if (!serverId) return
    
    // Debounce rapid calls (minimum 2 seconds between requests)
    const now = Date.now()
    if (now - lastFetchRef.current < 2000) {
      return
    }
    lastFetchRef.current = now
    
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    const controller = new AbortController()
    abortControllerRef.current = controller
    
    try {
      setError(null)

      // Fetch server status, activity, and real system metrics
      const [statusResponse, activityResponse, systemMetricsResponse] = await Promise.all([
        api.get(`/servers/${serverId}`),
        api.get(`/servers/${serverId}/activity?limit=20`),
        null,// monitoringApi.getSystemMetrics().catch(() => null) // Don't fail if system metrics unavailable
      ])

      // Check if request was cancelled
      if (controller.signal.aborted) return

      // Extract data from responses
      const serverData = statusResponse as any
      const activityData = activityResponse as any
      const systemData = systemMetricsResponse as SystemMonitoringResponse | null

      // Store system monitoring data
      setSystemMonitoring(systemData)

      // Get real metrics for this server if available
      const realServerMetrics = systemData?.servers?.[serverId]
      const hasRealMetrics = realServerMetrics !== undefined

      // Build enhanced metrics combining server data with real system metrics
      const enhancedMetrics: ServerMetrics = {
        // Use real metrics when available, fallback to server metrics
        cpu_usage: hasRealMetrics ? realServerMetrics.cpu_percent : (serverData.metrics?.cpu_usage || 0),
        memory_usage: hasRealMetrics ? realServerMetrics.memory_percent : (serverData.metrics?.memory_usage || 0),
        request_count: serverData.metrics?.request_count || 0,
        response_time_avg: serverData.metrics?.response_time_avg || 0,
        error_rate: serverData.metrics?.error_rate || 0,
        cache_hit_rate: serverData.metrics?.cache_hit_rate || 0,
        active_connections: serverData.metrics?.active_connections || 0,
        uptime: serverData.metrics?.uptime || 0,
        // Real system metrics (when available)
        process_id: realServerMetrics?.process_id,
        memory_mb: realServerMetrics?.memory_mb,
        process_status: realServerMetrics?.status,
        monitoring_source: hasRealMetrics ? 'real_system_metrics' : 'simulated_metrics',
      }

      // Build the status object with enhanced structure
      const serverStatus: ServerStatus = {
        server_id: serverId,
        status: serverData.status || 'running',
        port: serverData.port || 0,
        last_activity: serverData.last_activity || new Date().toISOString(),
        metrics: enhancedMetrics,
        logs: activityData.logs || [],
        // Add system-wide metrics for context
        system_metrics: systemData ? {
          cpu_percent: systemData.system.cpu_percent,
          memory_percent: systemData.system.memory_percent,
          memory_total: systemData.system.memory_total,
        } : undefined,
      }

      setStatus(serverStatus)
      setIsConnected(true)
      setIsLoading(false)
      
      // Call status change callback using ref to avoid dependency issues
      if (callbacksRef.current.onStatusChange) {
        callbacksRef.current.onStatusChange(serverStatus)
      }
      
    } catch (error: any) {
      // Don't handle errors for cancelled requests
      if (controller.signal.aborted) return
      
      console.error('Failed to fetch server data:', error)
      const errorMessage = error.response?.data?.details || error.message || 'Failed to fetch server data'
      const errorObj = new Error(errorMessage)
      setError(errorObj)
      setIsConnected(false)
      setIsLoading(false)
      
      // Call error callback using ref
      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(errorObj)
      }
    }
  }, [serverId]) // Only depend on serverId

  // Start monitoring - stable callback
  const startMonitoring = useCallback(() => {
    if (!enabled || !serverId) return

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Initial fetch
    setIsLoading(true)
    fetchServerStatus()

    // Set up polling with the specified interval
    intervalRef.current = setInterval(() => {
      fetchServerStatus()
    }, interval)

    console.log(`Started monitoring server ${serverId} with ${interval}ms interval (real metrics enabled)`)
  }, [enabled, serverId, interval, fetchServerStatus])

  // Stop monitoring - stable callback
  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    
    setIsConnected(false)
    console.log(`Stopped monitoring server ${serverId}`)
  }, [serverId])

  // Restart monitoring with new interval
  const restartMonitoring = useCallback((newInterval?: number) => {
    stopMonitoring()
    // Small delay to ensure cleanup is complete
    setTimeout(() => startMonitoring(), 100)
  }, [stopMonitoring, startMonitoring])

  // Manually refresh data
  const refresh = useCallback(() => {
    lastFetchRef.current = 0 // Reset debounce
    fetchServerStatus()
  }, [fetchServerStatus])

  // Effect to start/stop monitoring based on enabled state and serverId
  useEffect(() => {
    if (enabled && serverId) {
      startMonitoring()
    } else {
      stopMonitoring()
    }

    // Cleanup on unmount or dependency change
    return () => {
      stopMonitoring()
    }
  }, [serverId, enabled]) // Removed function dependencies to prevent constant re-starting

  // Separate effect for interval changes to avoid restart on every interval change
  useEffect(() => {
    if (enabled && serverId && intervalRef.current) {
      // Only restart if interval actually changed and monitoring is active
      stopMonitoring()
      setTimeout(() => startMonitoring(), 100)
    }
  }, [interval])

  // Pause monitoring when page is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopMonitoring()
      } else if (enabled && serverId) {
        startMonitoring()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, serverId, startMonitoring, stopMonitoring])

  return {
    status,
    isLoading,
    error,
    isConnected,
    systemMonitoring,
    refresh,
    startMonitoring,
    stopMonitoring,
    restartMonitoring
  }
} 